// Pipeline widget — "needs you now" (Ship 1, Aug 25, 2026).
//
// Telemetry-driven design: pipeline↔deal pogo is the app's #1 motion
// (3,000 + 2,581 hops/60d) and reps touch ~6 deals/day, so this widget IS those
// deals — ranked rows, not a funnel. Ranking: today's adjuster meetings first,
// then aging action stages oldest-first, then cold pursuables (7d+). Comms is
// 87% of tab-targeted deal opens but mostly reply-checking, so 💬 opens the
// full thread in context; 📞 is a native tel: link (the quick-compose buttons
// measured zero use).
//
// Data rules: counts/rows are NOT money math — one lean scoped deals query.
// Every select carries the explicit owner/split .or() (RLS is enforcement,
// never scoping). Book value deliberately lives in the My Business zone below,
// not here — one money surface, no scope drift.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { daysInStage } from "../../../lib/dealAge";
import { PURSUABLE_STAGES } from "../../../lib/playScope";
import { extractRawTime } from "../../../lib/timeUtils";
import { logCardClick } from "../../../lib/uiEvents";

import { NON_LIVE_STAGES, STAGE_BUCKETS } from "./stageBuckets";

const ACTION_STAGES = new Set([
  "install_meeting",
  "build_submitted",
  "adjuster_meeting_ready",
  "pre_inspection_ready",
]);

const ACTION_LABELS: Record<string, string> = {
  install_meeting: "Install mtg",
  build_submitted: "Build submitted",
  adjuster_meeting_ready: "Adjuster ready",
  pre_inspection_ready: "PRE ready",
};

const COLD_DAYS = 7;
const MAX_ROWS = 6;

interface PipeRow {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
  homeowner_phone: string | null;
  stage: string | null;
  stage_entered_at: string | null;
  adjuster_meeting_date: string | null;
  reinspection_adjuster_meeting_date: string | null;
}

interface RankedRow {
  deal: PipeRow;
  chip: string;
  tone: "teal" | "amber" | "rose";
}

const pad = (n: number) => String(n).padStart(2, "0");
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Fake-UTC meeting timestamps: raw HH:MM extraction, never new Date().
function fmtMeetTime(ts: string | null): string {
  const raw = extractRawTime(ts);
  if (!raw) return "";
  const [h, m] = raw.split(":").map(Number);
  const ampm = h >= 12 ? "p" : "a";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return m ? `${hr}:${pad(m)}${ampm}` : `${hr}${ampm}`;
}

export default function PipelineWidget({
  scopeIds,
  scopeNote,
}: {
  /** owner/split ids to scope to; null = no owner filter (exec/company view). */
  scopeIds: string[] | null;
  /** Optional "Mine"/"Team" note when the page-level scope toggle is active. */
  scopeNote?: string | null;
}) {
  const [rows, setRows] = useState<PipeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const scopeKey = scopeIds ? scopeIds.join(",") : "all";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      // Paged past the 1,000-row PostgREST cap (repo rule for any bulk deals
      // reader), with the .order("id") tiebreaker so pages can't dup/skip.
      const PAGE = 1000;
      const all: PipeRow[] = [];
      for (let from = 0; ; from += PAGE) {
        let q = supabase
          .from("deals")
          .select(
            "id,homeowner_name,property_street,homeowner_phone,stage,stage_entered_at,adjuster_meeting_date,reinspection_adjuster_meeting_date",
          )
          .not("stage", "in", `(${NON_LIVE_STAGES.join(",")})`)
          .order("id")
          .range(from, from + PAGE - 1);
        if (scopeIds && scopeIds.length) {
          const ids = scopeIds.join(",");
          q = q.or(`owner_id.in.(${ids}),split_rm_id.in.(${ids})`);
        }
        const { data, error } = await q;
        if (cancelled) return;
        if (error || !data) break;
        all.push(...(data as PipeRow[]));
        if (data.length < PAGE) break;
      }
      if (!cancelled) {
        setRows(all);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const { ranked, coldCount, bucketCounts } = useMemo(() => {
    const today = localToday();
    const pursuable = new Set<string>(PURSUABLE_STAGES as readonly string[]);
    const picked = new Map<string, RankedRow>();

    // 1 — meetings today (fake-UTC date columns compared as naive strings)
    for (const d of rows) {
      const meet = String(d.adjuster_meeting_date ?? "").slice(0, 10) === today;
      const reinsp = String(d.reinspection_adjuster_meeting_date ?? "").slice(0, 10) === today;
      if (!meet && !reinsp) continue;
      const time = meet
        ? fmtMeetTime(d.adjuster_meeting_date)
        : fmtMeetTime(d.reinspection_adjuster_meeting_date);
      picked.set(d.id, {
        deal: d,
        chip: `📅 ${reinsp && !meet ? "2nd adj" : "Adjuster"} today${time ? ` ${time}` : ""}`,
        tone: "teal",
      });
    }

    // 2 — aging action stages, oldest first
    const aging = rows
      .filter((d) => !picked.has(d.id) && d.stage && ACTION_STAGES.has(d.stage))
      .sort((a, b) => String(a.stage_entered_at ?? "9").localeCompare(String(b.stage_entered_at ?? "9")));
    for (const d of aging) {
      if (picked.size >= MAX_ROWS) break;
      const days = daysInStage(d);
      picked.set(d.id, {
        deal: d,
        chip: `✍️ ${ACTION_LABELS[d.stage ?? ""] ?? "Action"} · ${days}d`,
        tone: days >= 30 ? "rose" : "amber",
      });
    }

    // 3 — cold pursuables, coldest first
    const cold = rows
      .filter((d) => d.stage && pursuable.has(d.stage) && daysInStage(d) >= COLD_DAYS)
      .sort((a, b) => daysInStage(b) - daysInStage(a));
    for (const d of cold) {
      if (picked.size >= MAX_ROWS) break;
      if (picked.has(d.id)) continue;
      picked.set(d.id, { deal: d, chip: `🥶 Cold ${daysInStage(d)}d`, tone: "rose" });
    }

    const counts = STAGE_BUCKETS.map((b) => ({
      label: b.label,
      n: rows.filter((d) => d.stage && b.stages.includes(d.stage)).length,
    })).filter((b) => b.n > 0);

    return { ranked: [...picked.values()], coldCount: cold.length, bucketCounts: counts };
  }, [rows]);

  const toneClass: Record<RankedRow["tone"], string> = {
    teal: "bg-teal-50 text-teal-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-2">
        <span>📋</span>
        <span className="font-bold text-sm text-bardin-ink">My Pipeline</span>
        {scopeNote && (
          <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{scopeNote}</span>
        )}
        <span className="ml-auto text-xs font-bold text-slate-500">
          {loading ? "…" : `${rows.length} live`}
        </span>
      </div>

      {bucketCounts.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3 text-[10px] font-bold">
          {bucketCounts.map((b) => (
            <span key={b.label} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
              {b.label} {b.n}
            </span>
          ))}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-9 rounded-lg bg-slate-100 animate-pulse" />
          ))}
        </div>
      ) : ranked.length === 0 ? (
        <div className="text-xs text-slate-400 py-3 text-center">
          ✓ Nothing urgent — no meetings today, nothing aging, nothing cold.
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {ranked.map((r) => (
            <div key={r.deal.id} className="flex items-center gap-2 py-2">
              <Link
                to={`/deal/${r.deal.id}`}
                onClick={() => logCardClick("widget_pipeline_row")}
                className="flex-1 min-w-0"
              >
                <div className="text-[13px] font-bold text-bardin-ink truncate">
                  {r.deal.homeowner_name ?? "—"}
                </div>
                <div className="text-[11px] text-slate-400 truncate">
                  {r.deal.property_street ?? ""}
                </div>
              </Link>
              <span
                className={`px-2 py-0.5 rounded-md text-[10px] font-extrabold whitespace-nowrap ${toneClass[r.tone]}`}
              >
                {r.chip}
              </span>
              <div className="flex gap-1 flex-none">
                {r.deal.homeowner_phone && (
                  <a
                    href={`tel:${r.deal.homeowner_phone}`}
                    onClick={() => logCardClick("widget_pipeline_call")}
                    className="w-7 h-7 rounded-lg border border-slate-200 flex items-center justify-center text-xs hover:border-bardin-orange"
                    title="Call homeowner"
                  >
                    📞
                  </a>
                )}
                <Link
                  to={`/deal/${r.deal.id}?tab=comms`}
                  onClick={() => logCardClick("widget_pipeline_comms")}
                  className="w-7 h-7 rounded-lg border border-slate-200 flex items-center justify-center text-xs hover:border-bardin-orange"
                  title="Open the text thread"
                >
                  💬
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mt-3 pt-2.5 border-t border-slate-100">
        {coldCount > 0 && (
          <Link
            to="/run-the-play"
            onClick={() => logCardClick("widget_pipeline_cold")}
            className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 text-[10px] font-bold"
          >
            ▶️ {coldCount} cold pursuable{coldCount === 1 ? "" : "s"} — build a route
          </Link>
        )}
        <Link
          to="/my-pipeline"
          onClick={() => logCardClick("widget_pipeline_open")}
          className="ml-auto text-[11px] font-bold text-bardin-orange"
        >
          Open My Pipeline ›
        </Link>
      </div>
    </div>
  );
}
